import React from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';

import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import NavBar from '@/components/layout/NavBar/NavBar';
import Footer from '@/components/layout/Footer/Footer';
import ProtectedRoutes from '@/components/common/ProtectedRoutes';
import AdminRoutes from '@/components/common/AdminRoutes';
import PublicRoutes from '@/components/common/PublicRoutes';
import AuthRestrictedRoutes from '@/components/common/AuthRestrictedRoutes';
import TermlyCMP from '@/components/common/TermlyCMP';
import Homepage from '@/features/home/HomePage';
import SignUp from '@/features/auth/components/SignUp';
import ForgotPassword from '@/features/auth/components/ForgotPassword';
import LogIn from '@/features/auth/components/LogIn';
import ProfilePage from '@/features/profile/components/ProfilePage';
import CreateProfile from '@/features/profile/components/CreateProfile';
import EditProfile from '@/features/profile/components/EditProfile';
import SetupUsername from '@/features/profile/components/SetupUsername';
import About from '@/features/about/About';
import ProjectUpdates from '@/features/project-updates/ProjectUpdates';
import ContactUs from '@/features/contact/ContactUs';
import MainPolicies from '@/features/policies/MainPolicies';
import BiliChat from '@/features/chat/BiliChat';
import Dashboard from '@/features/dashboard/Dashboard';
import AdvancedQuery from '@/features/advanced-query/AdvancedQuery';
import Communities from '@/features/communities/Communities';
import CommunityGuidelines from '@/features/communities/CommunityGuidelines';
import CommunityPage from '@/features/communities/CommunityPage';
import PostDetailPage from '@/features/communities/PostDetailPage';
import SpotlightLanding from '@/features/spotlights/SpotlightLanding';
import SpotlightPage from '@/features/spotlights/SpotlightPage';
import FeedbackPage from '@/features/feedback/FeedbackPage';
import AdminDashboard from '@/features/admin/AdminDashboard';
import OverviewAdmin from '@/features/admin/OverviewAdmin';
import UsersAdmin from '@/features/admin/UsersAdmin';
import CommunitiesAdmin from '@/features/admin/CommunitiesAdmin';
import ModerationAdmin from '@/features/admin/ModerationAdmin';
import ModeratorDashboard from '@/features/moderator/ModeratorDashboard';
import ModeratorModeration from '@/features/moderator/ModeratorModeration';
import ModeratorMembers from '@/features/moderator/ModeratorMembers';
import ModeratorRoutes from '@/components/common/ModeratorRoutes';
import ChatWidget from '@/features/chat/components/ChatWidget';
import PageNotFound from '@/components/common/PageNotFound';
import ContinueTourBanner from '@/components/common/ContinueTourBanner';
import ScrollToTop from '@/components/common/ScrollToTop';
import DataSourceDashboard from '@/features/admin/DataSourceDashboard';
import DataDiscovery from '@/features/data-discovery/DataDiscovery';
import DataSourceDetail from '@/features/data-discovery/DataSourceDetail';
import DataProductDetail from '@/features/data-discovery/DataProductDetail';
import OrganizationDetail from '@/features/data-discovery/OrganizationDetail';
import TakedownPage from '@/features/takedown/TakedownPage';
import PublicProfile from '@/features/profile/components/PublicProfile';
// import Logout from '@/features/auth/components/Logout'; // Uncomment when Logout is ready

function App() {
    const location = useLocation();
    const isChatRoute = location.pathname === '/bili-chat';

    return (
        <div className="App">
            <TermlyCMP
                websiteUUID="4015b0f5-4d78-43f2-a83d-14d3dd043034"
                autoBlock={true}
            />
            <header id="App-header">
                <NavBar />
            </header>
            <ContinueTourBanner />
            <main
                id="App-main"
                className={isChatRoute ? 'overflow-hidden' : ''}
            >
                <ScrollToTop />
                <Routes>
                    {/* Public routes: accessible to everyone (wrapped in <PublicRoutes>) */}
                    <Route
                        path="/"
                        element={
                            <PublicRoutes>
                                <Homepage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/about"
                        element={
                            <PublicRoutes>
                                <About />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/project-updates"
                        element={
                            <PublicRoutes>
                                <ProjectUpdates />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/contact"
                        element={
                            <PublicRoutes>
                                <ContactUs />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/spotlights"
                        element={
                            <PublicRoutes>
                                <SpotlightLanding />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/spotlights/:topic"
                        element={<SpotlightPage />}
                    />
                    <Route
                        path="/policies/*"
                        element={
                            <PublicRoutes>
                                <MainPolicies />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/feedback"
                        element={
                            <PublicRoutes>
                                <FeedbackPage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/data"
                        element={
                            <PublicRoutes>
                                <DataDiscovery />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/data/sources/:id"
                        element={
                            <PublicRoutes>
                                <DataSourceDetail />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/data/products/:id"
                        element={
                            <PublicRoutes>
                                <DataProductDetail />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/data/organizations/:id"
                        element={
                            <PublicRoutes>
                                <OrganizationDetail />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/takedown"
                        element={
                            <PublicRoutes>
                                <TakedownPage />
                            </PublicRoutes>
                        }
                    />
                    <Route
                        path="/users/:username"
                        element={
                            <PublicRoutes>
                                <PublicProfile />
                            </PublicRoutes>
                        }
                    />

                    {/* Auth-Restricted Routes (only for unauthenticated users) */}
                    <Route
                        path="/login"
                        element={
                            <AuthRestrictedRoutes>
                                <LogIn />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/signup"
                        element={
                            <AuthRestrictedRoutes>
                                <SignUp />
                            </AuthRestrictedRoutes>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <AuthRestrictedRoutes>
                                <ForgotPassword />
                            </AuthRestrictedRoutes>
                        }
                    />

                    {/* Protected Routes (only for authenticated users) */}
                    <Route
                        path="/create-profile"
                        element={
                            <ProtectedRoutes>
                                <CreateProfile />
                            </ProtectedRoutes>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoutes>
                                <ProfilePage />
                            </ProtectedRoutes>
                        }
                    />

                    <Route
                        path="/profile/edit"
                        element={
                            <ProtectedRoutes>
                                <EditProfile />
                            </ProtectedRoutes>
                        }
                    />

                    <Route
                        path="/setup-username"
                        element={
                            <ProtectedRoutes>
                                <SetupUsername />
                            </ProtectedRoutes>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoutes>
                                <Dashboard />
                            </ProtectedRoutes>
                        }
                    />

                    <Route
                        path="/bili-chat"
                        element={
                            <ProtectedRoutes>
                                <BiliChat />
                            </ProtectedRoutes>
                        }
                    />

                    {/* Legacy /expert-query is superseded by the Advanced
                        Query page; redirect any old links/bookmarks. */}
                    <Route
                        path="/expert-query"
                        element={<Navigate to="/advanced-query" replace />}
                    />

                    <Route
                        path="/advanced-query"
                        element={
                            <ProtectedRoutes>
                                <AdvancedQuery />
                            </ProtectedRoutes>
                        }
                    />

                    <Route
                        path="/communities"
                        element={
                            <PublicRoutes>
                                <Communities />
                            </PublicRoutes>
                        }
                    />

                    <Route
                        path="/communities/guidelines"
                        element={
                            <PublicRoutes>
                                <CommunityGuidelines />
                            </PublicRoutes>
                        }
                    />

                    <Route
                        path="/communities/:slug/posts/:postId"
                        element={
                            <PublicRoutes>
                                <PostDetailPage />
                            </PublicRoutes>
                        }
                    />

                    <Route
                        path="/communities/:slug"
                        element={
                            <PublicRoutes>
                                <CommunityPage />
                            </PublicRoutes>
                        }
                    />

                    {/* Admin Routes — all nested under the AdminDashboard shell */}
                    <Route
                        path="/admin"
                        element={
                            <AdminRoutes>
                                <AdminDashboard />
                            </AdminRoutes>
                        }
                    >
                        <Route
                            index
                            element={<Navigate to="overview" replace />}
                        />
                        <Route path="overview" element={<OverviewAdmin />} />
                        <Route path="users" element={<UsersAdmin />} />
                        <Route
                            path="data-sources"
                            element={<DataSourceDashboard />}
                        />
                        <Route
                            path="communities"
                            element={<CommunitiesAdmin />}
                        />
                        <Route
                            path="moderation"
                            element={<ModerationAdmin />}
                        />
                        {/* Redirect legacy /admin/reports → /admin/moderation */}
                        <Route
                            path="reports"
                            element={
                                <Navigate to="/admin/moderation" replace />
                            }
                        />
                    </Route>

                    {/* Moderator Routes — scoped to communities the user moderates */}
                    <Route
                        path="/moderator"
                        element={
                            <ModeratorRoutes>
                                <ModeratorDashboard />
                            </ModeratorRoutes>
                        }
                    >
                        <Route
                            index
                            element={<Navigate to="moderation" replace />}
                        />
                        <Route
                            path="moderation"
                            element={<ModeratorModeration />}
                        />
                        <Route path="members" element={<ModeratorMembers />} />
                    </Route>

                    <Route path="*" element={<PageNotFound />} />

                    {/* Uncomment when ready */}
                    {/*
                    <Route
                        path="/logout"
                        element={
                        <ProtectedRoutes>
                            <Logout />
                        </ProtectedRoutes>
                        }
                    />
                    */}
                </Routes>
            </main>
            <ChatWidget />
            {!isChatRoute && (
                <footer id="App-footer">
                    <Footer />
                </footer>
            )}
        </div>
    );
}

export default App;
